import { useEffect, useState } from 'preact/hooks'
import { landingPageTemplatesApi } from '@/api/landingPageTemplates'
import { getDisplayErrorMessages } from '@/utils/errorHandling'
import type { LandingPageVersionHtmlDto } from '@/types/landingPage'

export interface UseLandingPageTemplatePreviewResult {
    /** Both pages of the version a start from this template would copy now. Null while loading or closed. */
    preview: LandingPageVersionHtmlDto | null
    loading: boolean
    error: string | null
}

const FALLBACK = 'Unable to load the preview'

type PreviewRead = { preview: LandingPageVersionHtmlDto; error: null } | { preview: null; error: string }

interface SharedRead {
    read: Promise<PreviewRead>
    holders: number
}

/**
 * One read per template for everything showing it at once — a row's thumbnail and the full preview opened from it.
 * An entry lasts only while something holds it, so a later visit reads the version the template would copy then, and a
 * failed read is dropped as soon as it settles so the next request asks again.
 */
const sharedReads = new Map<string, SharedRead>()

function holdRead(workspaceId: string, webId: string): { key: string; entry: SharedRead } {
    const key = `${workspaceId}/${webId}`
    const existing = sharedReads.get(key)
    if (existing) {
        existing.holders++
        return { key, entry: existing }
    }
    const read: Promise<PreviewRead> = landingPageTemplatesApi.getTemplatePreview(workspaceId, webId).then(
        (response): PreviewRead => response.data
            ? { preview: response.data, error: null }
            : { preview: null, error: getDisplayErrorMessages(response.error, FALLBACK).join(' ') },
        (cause: unknown): PreviewRead => ({ preview: null, error: getDisplayErrorMessages(cause, FALLBACK).join(' ') }),
    ).then(result => {
        if (result.error !== null && sharedReads.get(key)?.read === read) sharedReads.delete(key)
        return result
    })
    const entry = { read, holders: 1 }
    sharedReads.set(key, entry)
    return { key, entry }
}

function releaseRead(key: string, entry: SharedRead) {
    entry.holders--
    if (entry.holders === 0 && sharedReads.get(key) === entry) sharedReads.delete(key)
}

/**
 * Loads a template's preview HTML (ORB-1103). Pass `webId` null while no preview is open; switching to another
 * template drops the previous one's HTML at once rather than showing it under the new name.
 */
export function useLandingPageTemplatePreview(workspaceId: string, webId: string | null): UseLandingPageTemplatePreviewResult {
    const [preview, setPreview] = useState<LandingPageVersionHtmlDto | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setPreview(null)
        setError(null)
        if (!webId) {
            setLoading(false)
            return
        }
        let cancelled = false
        setLoading(true)
        const { key, entry } = holdRead(workspaceId, webId)
        void entry.read.then(result => {
            if (cancelled) return
            setPreview(result.preview)
            setError(result.error)
            setLoading(false)
        })
        return () => {
            cancelled = true
            releaseRead(key, entry)
        }
    }, [workspaceId, webId])

    return { preview, loading, error }
}
